import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";

export type WasteContainer = ClientOutput["utilities"]["WasteContainer"];
export type WasteContainerRequest =
  ClientOutput["utilities"]["WasteContainerRequest"];
export type WasteType = ClientOutput["utilities"]["WasteType"];

export const WASTE_TYPE_LABELS: Record<WasteType, string> = {
  trash: "Trash",
  mixed_recyclables: "Recycling",
  compostable: "Compost",
};

export const WASTE_UNIT_LABELS = {
  25: "Yd3",
  23: "Gallons",
  39: "Liters",
} as const;
export const PERCENT_FULL_LABELS: Record<
  WasteContainer["average_percent_full"],
  string
> = {
  Zero: "0%",
  TwentyFive: "25%",
  Fifty: "50%",
  SeventyFive: "75%",
  Hundred: "100%",
};
export type SupportedWasteUnitId = keyof typeof WASTE_UNIT_LABELS;
export const isWasteContainerActive = (wasteContainer: WasteContainer) =>
  !wasteContainer.valid_end_date;

const wasteContainers = {
  useQuery,
  mutations: {
    usePost,
    usePut,
    useDelete,
  },
} as const;

export default wasteContainers;

const WASTE_CONTAINERS_QUERY_KEY = "waste-containers";

// endpoint requires {feature: "Utilities", access: "read"}
async function getWasteContainers(
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET(
    "/{site_id}/waste_containers",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQuery(siteId: number) {
  return useQueryWithAccessCheckLog<WasteContainer[]>(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getWasteContainers(siteId, checkFeatureAccessHeader),
    {
      queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function postWasteContainer(
  siteId: number,
  body: WasteContainerRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.POST(
    "/{site_id}/waste_containers",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      body,
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function usePost(siteId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (body: WasteContainerRequest, checkFeatureAccessHeader) =>
      postWasteContainer(siteId, body, checkFeatureAccessHeader),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
        }),
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function putWasteContainer(
  siteId: number,
  wasteContainerId: number,
  body: WasteContainerRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.PUT(
    "/{site_id}/waste_containers/{waste_container_id}",
    {
      params: {
        path: {
          site_id: siteId,
          waste_container_id: wasteContainerId,
        },
      },
      body,
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function usePut(siteId: number, wasteContainerId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (body: WasteContainerRequest, checkFeatureAccessHeader) =>
      putWasteContainer(
        siteId,
        wasteContainerId,
        body,
        checkFeatureAccessHeader
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
        }),
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function deleteWasteContainer(
  siteId: number,
  wasteContainerId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.DELETE(
    "/{site_id}/waste_containers/{waste_container_id}",
    {
      params: {
        path: {
          site_id: siteId,
          waste_container_id: wasteContainerId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useDelete(siteId: number, wasteContainerId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (_: void, checkFeatureAccessHeader) =>
      deleteWasteContainer(siteId, wasteContainerId, checkFeatureAccessHeader),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [WASTE_CONTAINERS_QUERY_KEY, siteId],
        }),
    }
  );
}
